import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const sections = [
  {
    id: "section-full-home-remodels",
    title: "Full Home Remodels",
    description:
      "Complete home transformations handled professionally from concept to completion.",
    images: Array.from({ length: 13 }, (_, i) => ({
      src: `/Images/Full-Home-Remodel/Full Home Remodel ${i + 1}.jpeg`,
    })),
  },
  {
    id: "section-adu-projects",
    title: "ADU Projects",
    description:
      "Accessory Dwelling Units including garage conversions and detached units.",
    images: Array.from({ length: 7 }, (_, i) => ({
      src: `/Images/ADU and Custom Homes/ADU and Custom Homes ${i + 1}.jpeg`,
    })),
  },
  {
    id: "section-hoa-projects",
    title: "HOA Projects",
    description:
      "Community and HOA construction projects completed with careful coordination, quality craftsmanship, and professional project management.",
    images: Array.from({ length: 8 }, (_, i) => ({
      src: `/Images/HOA/HOA ${i + 1}.jpeg`,
    })),
  },
  {
    id: "section-behind-the-scenes",
    title: "Behind the Scenes",
    description:
      "A look inside our process — from raw framing and rough-in systems to the structured execution that defines every Ashlar build.",
    images: Array.from({ length: 5 }, (_, i) => ({
      src: `/Images/Behind-the-Scenes/BTS-${i + 1}.jpeg`,
    })),
  },
];

const videos = [
  {
    src: "/Images/Videos/Video 1.mp4",
    poster: "/Videos/Thumbs/Ashlar Video 1 Thumb.jpeg",
    title: "Project Walkthrough",
  },
];

const PortfolioGallery = () => {
  const [lightbox, setLightbox] = useState(null);

  return (
    <>
      <section className="bg-white py-14 px-6">
        <div className="max-w-6xl mx-auto">

          {/* HEADER */}
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-gray-500 uppercase tracking-wider text-sm font-semibold">
              Photo Gallery
            </p>
            <h2 className="text-3xl md:text-5xl font-black text-black mt-2">
              Built From Rough to Perfect
            </h2>
          </motion.div>

          <div className="flex flex-col gap-20">

            {/* IMAGE SECTIONS */}
            {sections.map((section, sIdx) => (
              <motion.div
                key={sIdx}
                id={section.id}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
                variants={stagger}
              >

                {/* Section Header */}
                <motion.div variants={fadeUp} className="mb-6 text-center">
                  <h3 className="text-xl md:text-2xl font-bold text-[#212529]">
                    {section.title}
                  </h3>
                  <p className="text-[#6c757d] text-sm mt-1">
                    {section.description}
                  </p>
                </motion.div>

                {/* IMAGE GRID (CENTERED LAYOUT) */}
                <div className="flex flex-wrap justify-center gap-4">
                  {section.images.map((img, iIdx) => (
                    <motion.div
                      key={iIdx}
                      variants={fadeUp}
                      className="relative overflow-hidden rounded-2xl group cursor-pointer aspect-[4/3] w-full sm:w-[48%] md:w-[31%]"
                      onClick={() => setLightbox(img)}
                    >
                      <img
                        src={img.src}
                        alt=""
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />

                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-end p-4">
                        <p className="text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 translate-y-2 group-hover:translate-y-0">
                          {img.label}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ))}

            {/* VIDEO SECTION */}
            <motion.div
              id="section-videos"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.1 }}
              variants={stagger}
            >
              <motion.div variants={fadeUp} className="mb-6 text-center">
                <h3 className="text-xl md:text-2xl font-bold text-[#212529]">
                  Project Videos
                </h3>
                <p className="text-[#6c757d] text-sm mt-1">
                  Watch our builds come to life — walkthroughs, progress clips, and before & after reveals.
                </p>
              </motion.div>

              <div
                className={`${
                  videos.length === 1
                    ? "flex justify-center"
                    : "grid gap-6 grid-cols-1 sm:grid-cols-2 md:grid-cols-3"
                }`}
              >
                {videos.map((video, vIdx) => (
                  <motion.div
                    key={vIdx}
                    variants={fadeUp}
                    className={`flex flex-col gap-2 ${
                      videos.length === 1 ? "max-w-md w-full" : ""
                    }`}
                  >
                    <div className="relative overflow-hidden rounded-2xl aspect-[4/3] bg-[#1a1c1f]">
                      <video
                        src={video.src}
                        poster={video.poster}
                        controls
                        preload="metadata"
                        playsInline
                        className="w-full h-full object-cover rounded-2xl"
                      />
                    </div>

                    {video.title && (
                      <p className="text-[#212529] text-sm font-semibold text-center">
                        {video.title}
                      </p>
                    )}
                  </motion.div>
                ))}
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* LIGHTBOX */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            className="fixed inset-0 bg-black/90 flex flex-col items-center justify-center z-50 px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
          >
            <motion.img
              src={lightbox.src}
              alt=""
              className="max-h-[80vh] max-w-[90vw] object-contain rounded-xl"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default PortfolioGallery;